'use client'

import { paths } from '@/config/paths'
import { useAccount } from '@/domain/hooks/useAccount'
import { useUserPreferences } from '@/domain/state'
import { DashboardStatsLoader } from '@/features/dashboard/components/DashboardStatsLoader'
import { PageHeroStats } from '@/ui/molecules/page-hero/PageHeroStats'
import { PageLayout } from '@/ui/layouts/PageLayout'
import { PageHeroCompact } from '@/ui/molecules/page-hero/PageHeroCompact'
import { PillNav } from '@/ui/molecules/page-hero/PillNav'
import { Star } from 'lucide-react'
import { usePathname } from 'next/navigation'
import { Suspense } from 'react'

const dashboardTabs = [
  { label: 'Pooled', href: paths.dashboardPooled },
  { label: 'Earn', href: paths.dashboardEarn },
  { label: 'Euler', href: paths.dashboardLendBorrow },
  { label: 'Legacy', href: paths.dashboardIsolated },
  { label: 'Activity', href: paths.activity },
]

function SetFavoriteButton() {
  const account = useAccount()
  const pathname = usePathname()
  const { getFavoriteTab, setFavoriteTab, clearFavoriteTab } = useUserPreferences()

  if (!account) return null

  const favoriteTab = getFavoriteTab('dashboard', account)
  const isFavorite = favoriteTab === pathname

  return (
    <button
      type="button"
      onClick={() => {
        if (isFavorite) {
          clearFavoriteTab('dashboard', account)
        } else {
          setFavoriteTab('dashboard', account, pathname)
        }
      }}
      aria-label={isFavorite ? 'Remove favorite dashboard tab' : 'Set as favorite dashboard tab'}
      className="text-muted-foreground transition-colors hover:text-foreground"
    >
      <Star className={`h-4 w-4 ${isFavorite ? 'fill-primary text-primary' : ''}`} />
    </button>
  )
}

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const account = useAccount()
  const { getFavoriteTab } = useUserPreferences()

  const favoriteTab = getFavoriteTab('dashboard', account)

  const pillNavItems = dashboardTabs.map((tab) => ({
    label: tab.label,
    href: tab.href,
    active: pathname === tab.href,
    postfix: favoriteTab === tab.href ? <Star className="h-3 w-3 fill-primary text-primary" /> : undefined,
  }))

  return (
    <PageLayout className="max-w-6xl gap-4 px-3">
      <PageHeroCompact
        title="Dashboard"
        titleAdornment={<SetFavoriteButton />}
        statsSlot={
          <Suspense
            fallback={
              <PageHeroStats
                stats={[
                  { label: 'Supplied', value: '' },
                  { label: 'Borrowed', value: '' },
                  { label: 'Net Equity', value: '' },
                ]}
                loading
              />
            }
          >
            <DashboardStatsLoader />
          </Suspense>
        }
        pillNav={<PillNav items={pillNavItems} />}
      />
      {children}
    </PageLayout>
  )
}
